import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { CheckCheck, Eye, Bell } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import { getUserFacingErrorMessage } from "@/api/userFacingError";
import { ApiError } from "@/api/client";
import { AdminBreadcrumb } from "@/components/admin/AdminBreadcrumb";
import { FloatingToast } from "@/components/feedback/FloatingToast";
import { AlertCard } from "@/components/notifications/AlertCard";
import {
  ALERT_TYPE_LABELS,
  getAlertPreviewText,
  getSeenTransitionStatus,
  getAlertTypeLabel,
} from "@/components/notifications/alertCardRules";
import "@/components/notifications/notifications.css";
import { OperationalEmptyState } from "@/components/operational/OperationalEmptyState";
import { OperationalFilterSurface } from "@/components/operational/OperationalFilterSurface";
import {
  OperationalPageFrame,
  OperationalPageHeader,
  OperationalToolbar,
} from "@/components/operational/OperationalPageLayout";
import { OperationalPager } from "@/components/operational/OperationalPager";
import { OperationalQueryState } from "@/components/operational/OperationalQueryState";
import { OperationalSearchField } from "@/components/operational/OperationalSearchField";
import { OperationalSelect } from "@/components/operational/OperationalSelect";
import { InlineActionButton } from "@/components/ui/InlineActionButton";
import { CloseIconButton } from "@/components/ui/CloseIconButton";
import {
  useAlertCounts,
  useAlerts,
  useClearAlerts,
  useTrayAlerts,
  useUpdateAlertStatus,
} from "@/features/alert/hooks";
import type {
  Alert,
  AlertClearScope,
  AlertStatus,
  AlertType,
} from "@/features/alert/types";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useDialogFocus } from "@/hooks/useDialogFocus";
import { timeAgo } from "@/lib/timeAgo";

type NotificationView = "resolver" | "new" | "all";
type TypeFilter = AlertType | "all";

const PAGE_SIZE = 10;
const TYPE_OPTIONS = [
  { value: "all", label: "Todos os tipos" },
  ...Object.entries(ALERT_TYPE_LABELS).map(([value, label]) => ({
    value,
    label,
  })),
];

function NotificationDetails({
  alert,
  activeStatusFilter,
  onClose,
  onResolutionSuccess,
  onMutationPendingChange,
  bulkPending,
}: {
  alert: Alert;
  activeStatusFilter: AlertStatus | "all";
  onClose: () => void;
  onResolutionSuccess: (message: string) => void;
  onMutationPendingChange: (pending: boolean) => void;
  bulkPending: boolean;
}) {
  const dialogRef = useRef<HTMLElement>(null);
  const preview = getAlertPreviewText(alert);
  const [nestedDialogOpen, setNestedDialogOpen] = useState(false);
  useDialogFocus(dialogRef, onClose, { enabled: !nestedDialogOpen });

  return (
    <div className="notification-detail-layer">
      <button
        type="button"
        tabIndex={-1}
        aria-label="Fechar detalhes da notificação"
        className="notification-detail-backdrop"
        onClick={onClose}
      />
      <section
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Detalhes da notificação"
        className="notification-detail"
        tabIndex={-1}
      >
        <header className="notification-detail__header">
          <div>
            <h2>{preview.title}</h2>
            <p className="notification-detail__context">{preview.context}</p>
          </div>
          <CloseIconButton
            label="Fechar detalhes"
            iconSize={19}
            onClick={onClose}
          />
        </header>
        <div className="notification-detail__body">
          <AlertCard
            alert={alert}
            summaryInHeader
            activeStatusFilter={activeStatusFilter}
            onResolutionSuccess={onResolutionSuccess}
            onNestedDialogChange={setNestedDialogOpen}
            onMutationPendingChange={onMutationPendingChange}
            bulkPending={bulkPending}
          />
        </div>
      </section>
    </div>
  );
}

function viewLabel(view: NotificationView) {
  if (view === "resolver") return "A resolver";
  if (view === "new") return "Novas";
  return "Todas";
}

export function NotificacoesPage() {
  const navigate = useNavigate();
  const [view, setView] = useState<NotificationView>("new");
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");
  const [draftTypeFilter, setDraftTypeFilter] =
    useState<TypeFilter>(typeFilter);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [selectedAlertId, setSelectedAlertId] = useState<string | null>(null);
  const [detailMutationCount, setDetailMutationCount] = useState(0);
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const [feedback, setFeedback] = useState<{
    title: string;
    message?: string;
    tone?: "success" | "error";
  } | null>(null);
  const [failedClearScope, setFailedClearScope] = useState<AlertClearScope>();
  const [failedSeenId, setFailedSeenId] = useState<string>();
  const debouncedSearch = useDebouncedValue(search.trim(), 250);
  const [querySearch, setQuerySearch] = useState(debouncedSearch);
  const countsQuery = useAlertCounts();
  const clearAlerts = useClearAlerts();
  const markSeen = useUpdateAlertStatus();
  const detailMutationPending = detailMutationCount > 0;

  useEffect(() => setPage(0), [view, typeFilter]);

  useEffect(() => {
    setPage(0);
    setQuerySearch(debouncedSearch);
  }, [debouncedSearch]);

  useEffect(() => {
    if (!feedback || feedback.tone === "error") return;
    const timeout = window.setTimeout(() => setFeedback(null), 4200);
    return () => window.clearTimeout(timeout);
  }, [feedback]);

  const params = useMemo(
    () => ({
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
      search: querySearch || undefined,
      type: typeFilter === "all" ? undefined : typeFilter,
    }),
    [page, querySearch, typeFilter],
  );
  const trayView =
    view === "resolver" ? "actionable" : view === "new" ? "new" : undefined;
  const trayQuery = useTrayAlerts(
    { ...params, view: trayView },
    trayView !== undefined,
  );
  const listQuery = useAlerts(params, view === "all");
  const query = view === "all" ? listQuery : trayQuery;
  const alerts = query.data?.items ?? [];
  const selectedAlert = selectedAlertId
    ? alerts.find((alert) => alert.id === selectedAlertId)
    : undefined;
  const total = query.data?.total ?? 0;
  const hasData = alerts.length > 0 || total > 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const isFiltered = Boolean(search || typeFilter !== "all");

  useEffect(() => {
    if (!query.data || query.isPlaceholderData) return;
    const lastPage = Math.max(0, Math.ceil(query.data.total / PAGE_SIZE) - 1);
    if (page > lastPage) setPage(lastPage);
  }, [page, query.data, query.isPlaceholderData]);

  useEffect(() => {
    if (
      selectedAlertId &&
      query.data &&
      !query.isFetching &&
      !query.isPlaceholderData &&
      !selectedAlert
    ) {
      setSelectedAlertId(null);
    }
  }, [
    query.data,
    query.isFetching,
    query.isPlaceholderData,
    selectedAlert,
    selectedAlertId,
  ]);

  function clearFilters() {
    setDraftTypeFilter("all");
    setTypeFilter("all");
  }

  function clearAll(scope: AlertClearScope) {
    if (clearAlerts.isPending || markSeen.isPending || detailMutationPending)
      return;
    setFeedback(null);
    setFailedClearScope(undefined);
    setFailedSeenId(undefined);
    clearAlerts.mutate(scope, {
      onSuccess: ({ updated }) => (
        setFailedClearScope(undefined),
        setFeedback({
          title:
            scope === "terminal"
              ? "Notificações encerradas removidas"
              : "Notificações marcadas como lidas",
          message:
            updated === 1
              ? "1 notificação atualizada."
              : `${updated} notificações atualizadas.`,
          tone: "success",
        })
      ),
      onError: (error) => {
        setFailedClearScope(scope);
        setFeedback({
          title: "Não foi possível limpar as notificações",
          message: getUserFacingErrorMessage(
            error,
            "Tente novamente em alguns instantes.",
          ),
          tone: "error",
        });
      },
    });
  }

  function markAlertSeen(id: string, restoreKeyboardFocus = false) {
    if (markSeen.isPending || clearAlerts.isPending || detailMutationPending)
      return;
    const focusSelectedTab = () =>
      tabRefs.current
        .find((tab) => tab?.getAttribute("aria-selected") === "true")
        ?.focus();
    setFeedback(null);
    setFailedClearScope(undefined);
    setFailedSeenId(undefined);
    markSeen.mutate(
      { id, status: getSeenTransitionStatus() },
      {
        onSuccess: () => {
          setFeedback({
            title: "Notificação marcada como vista",
            tone: "success",
          });
          if (restoreKeyboardFocus) focusSelectedTab();
        },
        onError: (error) => {
          const stale =
            error instanceof ApiError && [404, 409].includes(error.status);
          setFailedSeenId(stale ? undefined : id);
          if (stale && restoreKeyboardFocus) focusSelectedTab();
          setFeedback({
            title: "Não foi possível marcar como vista",
            message: getUserFacingErrorMessage(
              error,
              "Tente novamente em alguns instantes.",
            ),
            tone: "error",
          });
        },
      },
    );
  }

  const tabs: { value: NotificationView; count?: number }[] = [
    { value: "resolver", count: countsQuery.data?.actionable_count },
    { value: "new", count: countsQuery.data?.unread_count },
    { value: "all", count: view === "all" ? total : undefined },
  ];

  function handleTabKeyDown(
    event: KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) {
    let nextIndex: number | undefined;
    if (event.key === "Home") nextIndex = 0;
    else if (event.key === "End") nextIndex = tabs.length - 1;
    else if (event.key === "ArrowRight") nextIndex = (index + 1) % tabs.length;
    else if (event.key === "ArrowLeft")
      nextIndex = (index - 1 + tabs.length) % tabs.length;
    if (nextIndex === undefined) return;

    event.preventDefault();
    setView(tabs[nextIndex]!.value);
    tabRefs.current[nextIndex]?.focus();
  }

  return (
    <OperationalPageFrame size="standard" className="notifications-page">
      <AdminBreadcrumb
        items={[
          { label: "Mais", onClick: () => navigate({ to: "/mais" }) },
          { label: "Notificações" },
        ]}
      />
      <OperationalPageHeader
        title="Notificações"
        actions={
          view === "all" ? undefined : (
            <InlineActionButton
              tone="neutral"
              style={{
                minHeight: 44,
                padding: "0.5rem 0.75rem",
                borderRadius: 10,
                fontFamily: "'DM Sans', sans-serif",
                fontSize: "0.75rem",
                letterSpacing: 0,
                transform: "none",
                boxShadow: "none",
              }}
              onClick={() =>
                clearAll(view === "resolver" ? "terminal" : "informational")
              }
              disabled={
                clearAlerts.isPending ||
                markSeen.isPending ||
                detailMutationPending ||
                (view === "resolver"
                  ? !countsQuery.data?.terminal_clearable_count
                  : !countsQuery.data?.unread_count)
              }
              icon={<CheckCheck size={15} />}
            >
              {clearAlerts.isPending
                ? "Limpando..."
                : view === "resolver"
                  ? "Limpar encerradas"
                  : "Marcar todas como lidas"}
            </InlineActionButton>
          )
        }
      />

      <div
        className="notifications-tabs"
        role="tablist"
        aria-label="Visão das notificações"
      >
        {tabs.map((tab, index) => (
          <button
            key={tab.value}
            type="button"
            role="tab"
            id={`notifications-${tab.value}-tab`}
            aria-controls={`notifications-${tab.value}-panel`}
            aria-selected={view === tab.value}
            tabIndex={view === tab.value ? 0 : -1}
            ref={(element) => {
              tabRefs.current[index] = element;
            }}
            onKeyDown={(event) => handleTabKeyDown(event, index)}
            onClick={() => setView(tab.value)}
          >
            <span>{viewLabel(tab.value)}</span>
            {tab.count !== undefined ? <b>{tab.count}</b> : null}
          </button>
        ))}
      </div>

      <div
        id={`notifications-${view}-panel`}
        role="tabpanel"
        aria-labelledby={`notifications-${view}-tab`}
      >
        <OperationalToolbar
          className="notifications-toolbar"
          search={
            <OperationalSearchField
              value={search}
              onChange={setSearch}
              placeholder="Buscar notificações..."
              ariaLabel="Buscar notificações"
            />
          }
          filters={
            <OperationalFilterSurface
              open={filtersOpen}
              activeCount={typeFilter === "all" ? 0 : 1}
              onOpenChange={setFiltersOpen}
              onApply={() => setTypeFilter(draftTypeFilter)}
              onCancel={() => setDraftTypeFilter(typeFilter)}
              onClear={clearFilters}
            >
              <label className="notifications-type-filter">
                <span>TIPO</span>
                <OperationalSelect
                  ariaLabel="Tipo de notificação"
                  value={draftTypeFilter}
                  options={TYPE_OPTIONS}
                  onChange={(value) => setDraftTypeFilter(value as TypeFilter)}
                  menuLayer="overlay"
                />
              </label>
            </OperationalFilterSurface>
          }
        />

        <OperationalQueryState
          hasData={hasData}
          isLoading={query.isLoading}
          isFetching={query.isFetching}
          error={hasData ? null : query.error}
          isFiltered={isFiltered}
          onRetry={() => void query.refetch()}
          renderSkeleton={() => (
            <section className="notifications-ledger notifications-ledger--state">
              <OperationalEmptyState
                icon={<Bell size={18} />}
                title="Carregando notificações..."
              />
            </section>
          )}
          renderEmpty={() => (
            <section className="notifications-ledger notifications-ledger--state">
              <OperationalEmptyState
                icon={<Bell size={18} />}
                title={
                  isFiltered
                    ? "Nenhuma notificação encontrada"
                    : view === "resolver"
                      ? "Tudo limpo"
                      : "Nenhuma notificação"
                }
                description={
                  isFiltered
                    ? "Ajuste a busca ou limpe os filtros."
                    : view === "resolver"
                      ? "Nenhuma notificação precisa da sua atenção."
                      : "Não há notificações nesta visão."
                }
                action={
                  isFiltered ? (
                    <button
                      type="button"
                      onClick={() => {
                        setSearch("");
                        clearFilters();
                      }}
                    >
                      Limpar filtros
                    </button>
                  ) : undefined
                }
              />
            </section>
          )}
        >
          <section
            className="notifications-ledger"
            data-testid="notification-ledger"
            aria-busy={query.isFetching && !query.isLoading}
          >
            <div role="table" aria-label="Notificações">
              <div className="notifications-ledger__header" role="row">
                <span role="columnheader">Notificação</span>
                <span role="columnheader">Contexto</span>
                <span role="columnheader">Recebida</span>
                <span role="columnheader">Ação</span>
              </div>
              <div className="notifications-ledger__body" role="rowgroup">
                {alerts.map((alert) => {
                  const preview = getAlertPreviewText(alert);
                  return (
                    <div
                      key={alert.id}
                      className="notifications-ledger__row"
                      role="row"
                      data-status={alert.status}
                    >
                      <div
                        className="notifications-ledger__notification"
                        role="cell"
                        data-label="Notificação"
                      >
                        <span>{getAlertTypeLabel(alert.alert_type)}</span>
                        <strong>{preview.title}</strong>
                      </div>
                      <div role="cell" data-label="Contexto">
                        {preview.context}
                      </div>
                      <div role="cell" data-label="Recebida">
                        <time
                          dateTime={alert.created_at}
                          title={alert.created_at}
                        >
                          {timeAgo(alert.created_at)}
                        </time>
                      </div>
                      <div
                        className="notifications-ledger__action"
                        role="cell"
                        data-label="Ação"
                      >
                        <button
                          type="button"
                          className="notifications-ledger__open"
                          disabled={markSeen.isPending}
                          onClick={() => setSelectedAlertId(alert.id)}
                          aria-label={`Abrir ${preview.title}`}
                        >
                          <Eye size={17} aria-hidden="true" />
                          <span className="notifications-ledger__open-label">
                            Abrir
                          </span>
                        </button>
                        {alert.status === "sent" ? (
                          <button
                            type="button"
                            className="notifications-ledger__seen"
                            aria-label={`Marcar ${preview.title} como vista`}
                            title="Marcar como vista"
                            aria-disabled={
                              markSeen.isPending ||
                              clearAlerts.isPending ||
                              detailMutationPending ||
                              undefined
                            }
                            aria-busy={
                              (markSeen.isPending &&
                                markSeen.variables?.id === alert.id) ||
                              undefined
                            }
                            onClick={(event) =>
                              markAlertSeen(alert.id, event.detail === 0)
                            }
                          >
                            <CheckCheck size={17} aria-hidden="true" />
                            <span>Marcar vista</span>
                          </button>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {totalPages > 1 ? (
              <OperationalPager
                variant="numbered"
                className="notifications-ledger__pager"
                page={page}
                pageCount={totalPages}
                totalItems={total}
                pageSize={PAGE_SIZE}
                isFetching={query.isFetching && !query.isLoading}
                onPageChange={setPage}
              />
            ) : null}
          </section>
        </OperationalQueryState>
      </div>

      {selectedAlert ? (
        <NotificationDetails
          alert={selectedAlert}
          activeStatusFilter={view === "new" ? "sent" : "all"}
          onClose={() => setSelectedAlertId(null)}
          onMutationPendingChange={(pending) =>
            setDetailMutationCount((count) =>
              Math.max(0, count + (pending ? 1 : -1)),
            )
          }
          bulkPending={
            clearAlerts.isPending || markSeen.isPending || detailMutationPending
          }
          onResolutionSuccess={(message) => {
            setSelectedAlertId(null);
            setFeedback({
              title: "Decisão registrada.",
              message,
              tone: "success",
            });
          }}
        />
      ) : null}

      {feedback ? (
        <FloatingToast
          title={feedback.title}
          message={feedback.message}
          tone={feedback.tone}
          actionLabel={
            feedback.tone === "error" ? "Tentar novamente" : undefined
          }
          onAction={
            feedback.tone === "error"
              ? failedSeenId
                ? () => markAlertSeen(failedSeenId, true)
                : failedClearScope
                  ? () => clearAll(failedClearScope)
                  : undefined
              : undefined
          }
          onDismiss={() => setFeedback(null)}
        />
      ) : hasData && query.error ? (
        <FloatingToast
          title="Não foi possível atualizar as notificações"
          message={getUserFacingErrorMessage(
            query.error,
            "Tente novamente em alguns instantes.",
          )}
          actionLabel="Tentar novamente"
          onAction={() => void query.refetch()}
        />
      ) : countsQuery.error ? (
        <FloatingToast
          title="Não foi possível carregar a contagem de notificações"
          message={getUserFacingErrorMessage(
            countsQuery.error,
            "Tente novamente em alguns instantes.",
          )}
          actionLabel="Tentar novamente"
          onAction={() => void countsQuery.refetch()}
        />
      ) : null}
    </OperationalPageFrame>
  );
}
